import {
  createCustomerRequest,
  fetchCustomerRequest,
  updateCustomerRequest,
  fetchCustomersRequest,
  createCustomerSignupRequest
} from "../api/index";
import { handleErrorRequest } from "../helpers/api";
import { actions as contactActions } from "./contacts";
import _ from "lodash";
import { createActions, handleActions } from "redux-actions";
import {
  payloadIdentity,
  loadingMutation,
  simplePayloadMutation,
  resetMutation
} from "../helpers/redux";
import { actions as scooterActions } from "./scooters";
import { createSelector } from "reselect";
import { getUser, actions as authActions } from "./auth";
import { getCurrentDealer } from "./dealers";
import fp from "lodash/fp";

const customersState = state => state.customers;
const userSelector = state => getUser(state);
const roleSelector = state => state.settings.role;
const currentDealerSelector = state => getCurrentDealer(state);

const getValue = key =>
  createSelector(
    customersState,
    customers => customers[key]
  );

export const selectors = {
  isLoadingSubmitCustomer: getValue("loadingSubmitCustomer"),
  isSuccessSubmitCustomer: getValue("successSubmitCustomer"),
  getFailureSubmitCustomer: getValue("failureSubmitCustomer"),
  getCurrentCustomer: createSelector(
    customersState,
    userSelector,
    roleSelector,
    currentDealerSelector,
    ({ currentCustomer }, user, role, currentDealer) => {
      if (role === "customer") {
        return fp.pipe(
          fp.getOr([], "customers"),
          fp.find(
            customer =>
              _.get(customer, "dealer.id", "") ===
              _.get(currentDealer, "id", null)
          )
        )(user);
      }
      return currentCustomer;
    }
  )
};

const createdActions = createActions({
  customers: {
    SUBMIT_CUSTOMER_ATTEMPT: null,
    SUBMIT_CUSTOMER_FAILURE: payloadIdentity("failMessage"),
    SUBMIT_CUSTOMER_SUCCESS: null,
    FETCH_CUSTOMER_ATTEMPT: null,
    FETCH_CUSTOMER_FAILURE: payloadIdentity("failMessage"),
    FETCH_CUSTOMER_SUCCESS: null,
    FETCH_CUSTOMERS_ATTEMPT: null,
    FETCH_CUSTOMERS_FAILURE: payloadIdentity("failMessage"),
    FETCH_CUSTOMERS_SUCCESS: null,
    SET_CURRENT_CUSTOMER: payloadIdentity("currentCustomer"),
    SET_CUSTOMERS: payloadIdentity("customers"),
    SET_CURRENT_PAGE: payloadIdentity("currentPage"),
    SET_TOTAL_PAGES: payloadIdentity("totalPages"),
    SET_FILTER: payloadIdentity("filter"),
    RESET_STATE: payloadIdentity("toOmit")
  }
});

const {
  submitCustomerAttempt,
  submitCustomerSuccess,
  submitCustomerFailure,
  fetchCustomerAttempt,
  fetchCustomerSuccess,
  fetchCustomerFailure,
  fetchCustomersAttempt,
  fetchCustomersSuccess,
  fetchCustomersFailure,
  setCustomers,
  setTotalPages
} = createdActions.customers;

const setCurrentCustomer = currentCustomer => (dispatch, getState) => {
  dispatch(createdActions.customers.setCurrentCustomer(null));
  dispatch(scooterActions.setScooters(null));
  dispatch(scooterActions.setCurrentScooter(null));
  dispatch(createdActions.customers.setCurrentCustomer(currentCustomer));
  const currentDealer = getState().dealers.currentDealer;
  if (currentCustomer) {
    if (currentCustomer.contacts) {
      dispatch(contactActions.setContacts(currentCustomer.contacts));
    } else {
      currentDealer &&
        dispatch(
          contactActions.fetchContacts({
            dealer_id: currentDealer.id,
            customer_id: currentCustomer.id
          })
        );
    }
  }
};

const submitCustomer = request => dispatch => {
  dispatch(submitCustomerAttempt());
  return request.then(({ response, error }) => {
    if (response) {
      dispatch(submitCustomerSuccess());
      return Promise.resolve(response.data);
    } else {
      dispatch(submitCustomerFailure(handleErrorRequest(error)));
      return Promise.reject({ error });
    }
  });
};

const createCustomer = ({ dealer_id, data }) => (dispatch, getState) => {
  let jwt = _.get(getState(), "auth.user.jwt", "");
  return dispatch(
    submitCustomer(
      createCustomerRequest({
        data: { customer: data },
        dealer_id,
        jwt
      })
    )
  );
};

const createCustomerSignup = ({ dealer_id, data }) => async dispatch => {
  try {
    const user = await dispatch(
      submitCustomer(
        createCustomerSignupRequest({
          data: { customer: data },
          dealer_id
        })
      )
    );
    dispatch(authActions.setUser(user));
    return Promise.resolve(user);
  } catch (err) {
    return Promise.reject(err);
  }
};

const fetchCustomer = ({ dealer_id, customer_id }) => (dispatch, getState) => {
  dispatch(fetchCustomerAttempt());
  let jwt = _.get(getState(), "auth.user.jwt", "");
  return fetchCustomerRequest({ customer_id, dealer_id, jwt }).then(
    ({ response, error }) => {
      if (response) {
        dispatch(fetchCustomerSuccess());
        dispatch(setCurrentCustomer(response.data));
      } else {
        dispatch(fetchCustomerFailure(handleErrorRequest(error)));
      }
    }
  );
};

const fetchCustomers = ({ dealer_id, query_string = "" }) => (
  dispatch,
  getState
) => {
  dispatch(fetchCustomersAttempt());
  dispatch(setCustomers(null));
  let jwt = _.get(getState(), "auth.user.jwt", "");
  return fetchCustomersRequest({ dealer_id, query_string, jwt }).then(
    ({ response, error }) => {
      if (response) {
        dispatch(setCustomers(response.data));
        dispatch(fetchCustomersSuccess());
        dispatch(
          setTotalPages(parseInt(response.headers["x-total-pages"], 10))
        );
        return Promise.resolve(response.data);
      } else {
        dispatch(fetchCustomersFailure(handleErrorRequest(error)));
        return Promise.reject(error);
      }
    }
  );
};

const fetchContactCustomers = () => (
  dispatch,
  getState
) => {
  dispatch(fetchCustomersAttempt());
  dispatch(setCustomers(null));
  let user = _.get(getState(), "auth.user", "");
    if (user.customer_for_contact && user.customer_for_contact.length > 0) {
      dispatch(setCustomers(user.customer_for_contact));
      dispatch(fetchCustomersSuccess());
      dispatch(setTotalPages(10));
    }
};

const updateCustomer = ({ dealer_id, customer_id, data }) => (
  dispatch,
  getState
) => {
  dispatch(submitCustomerAttempt());
  let jwt = _.get(getState(), "auth.user.jwt", "");
  return updateCustomerRequest({
    dealer_id,
    customer_id,
    data: { customer: data },
    jwt
  }).then(({ response, error }) => {
    if (response) {
      dispatch(submitCustomerSuccess());
      return Promise.resolve(response.data);
    } else {
      dispatch(submitCustomerFailure(handleErrorRequest(error)));
      return Promise.reject(error);
    }
  });
};

export const actions = {
  ...createdActions.customers,
  fetchCustomer,
  fetchCustomers,
  createCustomer,
  updateCustomer,
  setCurrentCustomer,
  createCustomerSignup,
  fetchContactCustomers
};

export const initialState = {
  loadingSubmitCustomer: false,
  failureSubmitCustomer: null,
  successSubmitCustomer: false,
  loadingFetchCustomer: false,
  failureFetchCustomer: null,
  successFetchCustomer: false,
  loadingFetchCustomers: false,
  failureFetchCustomers: null,
  successFetchCustomers: false,
  currentCustomer: null,
  customers: null,
  currentPage: 1,
  totalPages: 1,
  filter: null
};

export default handleActions(
  {
    customers: {
      SUBMIT_CUSTOMER_ATTEMPT: loadingMutation({
        name: "SubmitCustomer",
        type: "loading"
      }),
      SUBMIT_CUSTOMER_SUCCESS: loadingMutation({
        name: "SubmitCustomer",
        type: "success"
      }),
      SUBMIT_CUSTOMER_FAILURE: loadingMutation({
        name: "SubmitCustomer",
        type: "failure"
      }),
      FETCH_CUSTOMER_ATTEMPT: loadingMutation({
        name: "FetchCustomer",
        type: "loading"
      }),
      FETCH_CUSTOMER_SUCCESS: loadingMutation({
        name: "FetchCustomer",
        type: "success"
      }),
      FETCH_CUSTOMER_FAILURE: loadingMutation({
        name: "FetchCustomer",
        type: "failure"
      }),
      FETCH_CUSTOMERS_ATTEMPT: loadingMutation({
        name: "FetchCustomers",
        type: "loading"
      }),
      FETCH_CUSTOMERS_SUCCESS: loadingMutation({
        name: "FetchCustomers",
        type: "success"
      }),
      FETCH_CUSTOMERS_FAILURE: loadingMutation({
        name: "FetchCustomers",
        type: "failure"
      }),
      SET_CURRENT_CUSTOMER: simplePayloadMutation,
      SET_CUSTOMERS: simplePayloadMutation,
      SET_FILTER: simplePayloadMutation,
      SET_TOTAL_PAGES: simplePayloadMutation,
      SET_CURRENT_PAGE: simplePayloadMutation,
      RESET_STATE: resetMutation(initialState)
    }
  },
  initialState
);
